// ── Workspace Tasks page ──

import { api } from '../api.js';
import { checkAuth, getUser } from '../auth.js';
import { showToast, esc, projectSwatchColor, schemeSurfaceStyle } from '../ui.js';
import { formatDate, TYPES } from '../utils.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import { openTaskDetail, closeTaskDetail } from '../components/task-detail.js';
import { statusNames, statusLabel, statusBadgeAttrs, closedNames } from '../lib/statuses.js';
import { renderPager, clampOffset } from '../components/pager.js';
import { getQueryParams, setQueryParams, getNavigationId } from '../router.js';
import {
  renderBoardFilters, bindBoardFilters, unbindBoardFilters, syncFilterChips,
  captureFilterFocus, restoreFilterFocus, filtersToParams, filtersFromParams,
  workspaceQueryParams, pruneWorkspaceFilters, FILTER_DEFAULTS,
} from '../components/board-filters.js';

// ── Constants ──

const WS_SCOPE = { scope: 'workspace' };

const SORT_COLUMNS = ['project_name', 'task_key', 'title', 'status', 'assignee', 'priority', 'updated_at'];

// ── State ──

let state = {};
function resetState() {
  state = {
    tasks: [], total: 0, loading: false,
    // status default is filled in once loadWorkspaceStatuses() resolves (C1182) — excludes
    // whatever names hold the complete/canceled role across the union (C1187).
    filters: { ...FILTER_DEFAULTS, projects: [] },
    sortCol: 'updated_at', sortDir: 'desc',
    groupBy: 'none',
    limit: 50, offset: 0,
    projects: [],
    statuses: [],
    members: [],   // accepted members of the selected (default: all readable) projects — assignee dropdown + names (TPT453)
    tags: [],      // tag names of the selected projects — tag typeahead (TPT453)
    optionsSeq: 0, // guards loadFilterOptions() against out-of-order responses
    projectKey: '', // joined filters.projects the current options were loaded for
    root: null,
    navId: null,   // router navigation id this list was mounted under (see isLive())
    filterBinding: null, // token of the latest bindBoardFilters() (TPT643), passed to the teardown's unbind
    detailToken: null,   // owner token of the latest drawer this list opened (TPT647), passed to closeTaskDetail()
  };
}
resetState();

// True while `owner` is the mounted list and no navigation happened since it mounted. Checked
// after every await that can run during the mount: a list the user left (to another page, or a
// newer mount of this list) must not write state, render, bind or touch the URL.
function isLive(owner) {
  return state === owner && getNavigationId() === owner.navId;
}

function getWorkspaceElement(id) {
  return state.root?.querySelector('#' + id) || document.getElementById(id);
}

// ── Helpers ──

// Badge color for a project (C1266) — its color_scheme swatch, or a per-id hash color
// while color_scheme is still 'default'. See projectSwatchColor() in ui.js.
function projectColor(projectId) {
  const project = state.projects.find(p => p.id === projectId);
  return projectSwatchColor(project && project.color_scheme, projectId);
}

// Inline-style fragment (C1472) carrying --proj-bg/--proj-text/--proj-muted/--proj-track
// for this row's .ws-project-badge — '' while color_scheme is still 'default'. Mirrors
// dashboard.js's projectSurface(); see schemeSurfaceStyle() in ui.js.
function projectSurface(projectId) {
  const project = state.projects.find(p => p.id === projectId);
  return schemeSurfaceStyle(project && project.color_scheme);
}

function buildQueryString() {
  const p = workspaceQueryParams(state.filters);
  p.set('limit', state.limit);
  p.set('offset', state.offset);
  return p.toString();
}

// Statuses, members and tags the filter bar may offer, narrowed server-side to the selected
// projects (default: every readable one) — GET /api/workspace/filter-options (TPT453).
// Returns null when a newer request superseded this one, the list is no longer live, or the
// request failed. The seq is compared on its own state object: a newer mount restarts the count.
async function loadFilterOptions(projectIds) {
  const owner = state;
  const seq = ++owner.optionsSeq;
  const current = () => isLive(owner) && seq === owner.optionsSeq;
  const qs = projectIds.length ? '?project_id=' + projectIds.join(',') : '';
  try {
    const res = await api.get('/workspace/filter-options' + qs);
    return current() ? res : null;
  } catch (err) {
    if (current()) showToast(err.message || 'Failed to load filter options', 'error');
    return null;
  }
}

function defaultStatusNames(statuses) {
  const closed = new Set(closedNames(statuses));
  return statusNames(statuses).filter(n => !closed.has(n));
}

// Adopt a filter-options response: swap the option lists and drop any selected project,
// status, assignee or tag the new lists do not offer. Returns true when the selection changed.
function applyFilterOptions(res) {
  state.statuses = res.statuses || [];
  state.members = res.members || [];
  state.tags = res.tags || [];
  const pruned = pruneWorkspaceFilters(state.filters, {
    projects: state.projects.map(p => p.id),
    statuses: state.statuses,
    members: state.members,
    tags: state.tags,
  }, defaultStatusNames(state.statuses));
  state.filters = pruned.filters;
  state.projectKey = state.filters.projects.join(',');
  return pruned.changed;
}

function assigneeLabel(task) {
  if (!task.assignee) return '<span class="text-muted">—</span>';
  const me = getUser();
  if (me && task.assignee === me.id) return '<span class="ws-assignee-me">Me</span>';
  const m = state.members.find(mm => mm.user_id === task.assignee);
  if (m) return '<span class="ws-assignee-id">' + esc(m.name || m.email || '#' + task.assignee) + '</span>';
  return '<span class="ws-assignee-id">#' + task.assignee + '</span>';
}

// ── Fetch ──

// One page at a time (TPT408): `reset` (the default — every filter change) goes back to
// the first page; `reset:false` refetches the current offset (pager arrows, task-detail
// close). A stale offset past the end (the count shrank under a new total) is clamped
// onto the last real page and refetched once, so the pager never shows an empty page.
async function fetchTasks({ reset = true } = {}) {
  if (state.loading) return;
  const owner = state;
  state.loading = true;
  if (reset) state.offset = 0;
  renderTableArea();
  renderPagination();

  try {
    let res = await api.get('/workspace/tasks?' + buildQueryString());
    if (!isLive(owner)) return;
    const clamped = clampOffset(state.offset, state.limit, Number(res.total) || 0);
    if (clamped !== state.offset) {
      state.offset = clamped;
      res = await api.get('/workspace/tasks?' + buildQueryString());
      if (!isLive(owner)) return;
    }
    state.tasks = res.tasks || [];
    state.total = Number(res.total) || state.tasks.length;
  } catch (err) {
    if (!isLive(owner)) return;
    showToast(err.message || 'Failed to load tasks', 'error');
  }

  state.loading = false;
  renderTableArea();
  renderPagination();
}

// Prev/next arrow: move one page and keep the list's top in view when the pager that was
// clicked has scrolled off the top (it sits in the filter bar above the table).
function goToPage(delta) {
  if (state.loading) return;
  const next = state.offset + delta * state.limit;
  if (next < 0 || next >= state.total) return;
  state.offset = next;
  void fetchTasks({ reset: false }).then(() => {
    const area = getWorkspaceElement('ws-table-area');
    if (area && area.getBoundingClientRect().top < 0) area.scrollIntoView({ block: 'start' });
  });
}

async function loadWorkspaceTaskData({ projects } = {}) {
  const owner = state;
  if (Array.isArray(projects)) {
    state.projects = projects;
  } else {
    let res = null;
    try {
      res = await api.get('/projects');
    } catch (_) { /* proceed without project filter */ }
    if (!isLive(owner)) return;
    if (res) state.projects = res.projects || [];
  }

  // Filters restored from the URL (TPT447). Projects the user cannot read are dropped first
  // (the options request would answer 403), then the options for that selection are loaded
  // and every status/assignee/tag they do not offer is pruned (TPT453). A missing `status`
  // param means "default" — every non-closed name (C1187) — while `status=all` is an
  // explicit empty selection.
  const params = getQueryParams();
  const parsed = filtersFromParams(params, WS_SCOPE);
  parsed.types = parsed.types.filter(t => TYPES.includes(t));
  const known = new Set(state.projects.map(p => p.id));
  parsed.projects = (parsed.projects || []).filter(id => known.has(id));
  state.filters = { ...FILTER_DEFAULTS, projects: [], ...parsed };

  const options = await loadFilterOptions(state.filters.projects);
  if (!options) return; // also null once the list is no longer live — never stamp another page's URL
  const changed = applyFilterOptions(options);
  if (!params.has('status')) state.filters.statuses = defaultStatusNames(state.statuses);
  if (changed) writeFiltersToUrl();
}

// ── Sort & Group ──

function getSortedTasks() {
  const col = state.sortCol;
  const dir = state.sortDir === 'asc' ? 1 : -1;
  return [...state.tasks].sort((a, b) => {
    let va = a[col], vb = b[col];
    if (va == null) va = '';
    if (vb == null) vb = '';
    if (col === 'updated_at' || col === 'created_at') {
      return dir * (new Date(va) - new Date(vb));
    }
    if (col === 'assignee' || col === 'priority') {
      return dir * ((+va || 0) - (+vb || 0));
    }
    return dir * String(va).localeCompare(String(vb));
  });
}

function groupTasks(sorted) {
  if (state.groupBy === 'none') return [{ key: '_all', label: '', tasks: sorted }];

  const map = new Map();
  for (const t of sorted) {
    let key, label;
    if (state.groupBy === 'project') {
      key = t.project_id; label = t.project_name || 'Unknown';
    } else if (state.groupBy === 'status') {
      key = t.status; label = statusLabel(t.status);
    } else if (state.groupBy === 'assignee') {
      key = t.assignee || 0;
      const me = getUser();
      label = !t.assignee ? 'Unassigned' : (me && t.assignee === me.id ? 'Me' : '#' + t.assignee);
    }
    if (!map.has(key)) map.set(key, { key, label, tasks: [] });
    map.get(key).tasks.push(t);
  }
  return [...map.values()];
}

// ── Render ──

function renderRow(t) {
  const color = projectColor(t.project_id);
  const tags = (t.tags || []).slice(0, 3);
  const extraTags = (t.tags || []).length - 3;
  return `<tr class="ws-row" data-task-key="${esc(t.task_key)}" data-project-id="${t.project_id}">
    <td><span class="ws-project-badge" style="--proj-color:${color}${projectSurface(t.project_id)}">${esc(t.project_name)}</span></td>
    <td class="ws-cell-key"><a class="task-key-link" href="#/projects/${t.project_id}/tasks/${encodeURIComponent(t.task_key)}" draggable="false" title="Open task page">${esc(t.task_key)}</a></td>
    <td class="ws-cell-title">${esc(t.title)}</td>
    <td><span class="badge ${statusBadgeAttrs(state.statuses.find(s => s.name === t.status)).className}" style="${statusBadgeAttrs(state.statuses.find(s => s.name === t.status)).style}">${esc(statusLabel(t.status))}</span></td>
    <td>${assigneeLabel(t)}</td>
    <td class="ws-cell-sprint">P${t.priority}</td>
    <td>${tags.map(tg => `<span class="tag">${esc(tg)}</span>`).join('')}${extraTags > 0 ? `<span class="text-muted text-sm">+${extraTags}</span>` : ''}</td>
    <td class="text-muted ws-cell-date">${formatDate(t.updated_at)}</td>
  </tr>`;
}

function renderTheadRow() {
  const cols = [
    { key: 'project_name', label: 'Project' },
    { key: 'task_key', label: 'Key' },
    { key: 'title', label: 'Title' },
    { key: 'status', label: 'Status' },
    { key: 'assignee', label: 'Member Assignee' },
    { key: 'priority', label: 'Sprint' },
    { key: null, label: 'Tags' },
    { key: 'updated_at', label: 'Updated' },
  ];
  return '<tr>' + cols.map(c => {
    if (!c.key) return `<th>${c.label}</th>`;
    const sorted = state.sortCol === c.key;
    const cls = ['ws-th-sortable'];
    if (sorted) cls.push('ws-sorted', 'ws-sorted-' + state.sortDir);
    return `<th class="${cls.join(' ')}" data-col="${c.key}">${c.label}</th>`;
  }).join('') + '</tr>';
}

function renderTableArea() {
  const area = getWorkspaceElement('ws-table-area');
  if (!area) return;

  if (state.loading) {
    area.innerHTML = '<div class="empty"><span class="spinner"></span></div>';
    return;
  }
  if (!state.tasks.length) {
    area.innerHTML = '<div class="empty text-muted">No tasks found</div>';
    return;
  }

  const sorted = getSortedTasks();
  const groups = groupTasks(sorted);

  if (state.groupBy === 'none') {
    area.innerHTML = `<table class="ws-table"><thead>${renderTheadRow()}</thead><tbody>${sorted.map(renderRow).join('')}</tbody></table>`;
  } else {
    area.innerHTML = groups.map(g => `
      <div class="ws-group">
        <div class="ws-group-header">
          <span class="ws-group-title">${esc(g.label)}</span>
          <span class="ws-group-count badge">${g.tasks.length}</span>
        </div>
        <table class="ws-table"><thead>${renderTheadRow()}</thead><tbody>${g.tasks.map(renderRow).join('')}</tbody></table>
      </div>`).join('');
  }

  bindTableEvents();
}

// Gmail-style "1–50 of 342" + chevrons in the filter bar's right edge (TPT408). Empty
// while there is nothing to page; both arrows greyed during a fetch.
function renderPagination() {
  const el = getWorkspaceElement('ws-pagination');
  if (!el) return;
  el.innerHTML = renderPager({ offset: state.offset, limit: state.limit, total: state.total, scope: 'ws', disabled: state.loading });
}

// Options for the shared board filter bar in workspace scope (TPT447): no sprint selector /
// Edit Board, a Project multi-select instead, and no status counts (the list is server-paged).
function filterOpts() {
  return {
    ...WS_SCOPE,
    showStatusCounts: false,
    projects: state.projects.map(p => ({ id: p.id, name: p.name, color: projectColor(p.id) })),
  };
}

function refreshFilterBar() {
  const el = getWorkspaceElement('ws-filters');
  if (!el) return;
  const focusSnap = captureFilterFocus();
  const opts = filterOpts();
  el.innerHTML = renderBoardFilters(state.filters, state.members, state.tags, [], [], state.statuses, opts);
  state.filterBinding = bindBoardFilters(state.filters, onFilterChange, state.members, state.tags, [], [], state.statuses, opts);
  restoreFilterFocus(focusSnap);
}

// Filters live in the URL query next to `tab=all`, so a reload restores them. Merge instead of
// replace: foreign keys survive, stale filter keys are dropped first.
function writeFiltersToUrl() {
  const params = getQueryParams();
  ['q', 'type', 'status', 'assignee', 'tag', 'sprint', 'project'].forEach(k => params.delete(k));
  filtersToParams(state.filters, WS_SCOPE).forEach((v, k) => params.set(k, v));
  setQueryParams(params);
}

function onFilterChange(filters, changedKey) {
  state.filters = filters;
  writeFiltersToUrl();
  // Search-only change: patch chips/Clear All, never rebuild the focused #bf-search (C1524).
  if (changedKey === 'q') syncFilterChips(state.filters, state.members, [], filterOpts());
  else refreshFilterBar();
  if (changedKey !== 'q' && state.filters.projects.join(',') !== state.projectKey) {
    void reloadOptionsThenFetch();
    return;
  }
  fetchTasks();   // reset:true → back to page 1
}

// The Project selection changed: refresh the bar's statuses/members/tags from the server for
// the new selection, prune what is no longer valid, then fetch page 1 (TPT453).
async function reloadOptionsThenFetch() {
  const root = state.root;
  const options = await loadFilterOptions(state.filters.projects);
  if (!options || state.root !== root) return;
  if (applyFilterOptions(options)) writeFiltersToUrl();
  else state.projectKey = state.filters.projects.join(',');
  refreshFilterBar();
  await fetchTasks();
}

// Reusable All Tasks content. The caller owns the surrounding page and chooses whether
// to include the standalone page header (C1276).
export function renderWorkspaceTaskList({ includeHeader = true } = {}) {
  return `${includeHeader ? `
    <div class="page-header" style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:0.75rem">
      <h2>All Tasks</h2>
      <div class="ws-controls">
        <select class="form-select form-select-sm" id="ws-group-by">
          <option value="none">No grouping</option>
          <option value="project">By Project</option>
          <option value="status">By Status</option>
          <option value="assignee">By Member Assignee</option>
        </select>
      </div>
    </div>` : ''}
    <div id="ws-filters"></div>
    <div class="ws-list-toolbar"><div class="ws-pagination" id="ws-pagination"></div></div>
    <div id="ws-table-area"><div class="empty"><span class="spinner"></span></div></div>`;
}

// ── Event binding ──

function bindListEvents() {
  // Group-by
  const groupSel = document.getElementById('ws-group-by');
  if (groupSel) {
    groupSel.addEventListener('change', () => {
      state.groupBy = groupSel.value;
      renderTableArea();
    });
  }

  // Pager arrows (TPT408) — scoped so the dashboard's My Tasks pager is not ours.
  const toolbar = state.root.querySelector('.ws-list-toolbar');
  if (toolbar) {
    toolbar.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-pager][data-pager-scope="ws"]');
      if (!btn || btn.disabled) return;
      goToPage(btn.dataset.pager === 'prev' ? -1 : 1);
    });
  }
}

function bindTableEvents() {
  const area = getWorkspaceElement('ws-table-area');
  if (!area) return;

  // Sort
  area.querySelectorAll('.ws-th-sortable').forEach(th => {
    th.addEventListener('click', () => {
      const col = th.dataset.col;
      if (state.sortCol === col) {
        state.sortDir = state.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        state.sortCol = col;
        state.sortDir = 'asc';
      }
      renderTableArea();
    });
  });

  // Row click → task detail
  area.querySelectorAll('.ws-row').forEach(row => {
    row.addEventListener('click', (e) => {
      if (e.target.closest('a')) return;
      const projectId = row.dataset.projectId;
      const taskKey = row.dataset.taskKey;
      state.detailToken = openTaskDetail(projectId, taskKey, {
        allTasks: state.tasks,
        onClose: () => fetchTasks({ reset: false }),
      });
    });
  });
}

function cleanupWorkspaceTaskList() {
  // Owner-scoped (TPT643, TPT647): a late teardown must not close or unbind what the board, the
  // dashboard or a newer list has open.
  closeTaskDetail(state.detailToken);
  unbindBoardFilters(state.filterBinding);
  resetState();
}

// Mount reusable All Tasks content into a page-owned container. Dashboard passes its
// already-loaded project list to avoid a duplicate request; statuses, members and tags always
// come from GET /workspace/filter-options (TPT453).
//
// A navigation while it loads stops it (isLive()): before the filter bar is bound it returns no
// teardown, since nothing needs releasing; after that it returns the teardown, which its caller
// runs at once (router.js for the page, activateDashboardTab() for the dashboard tab).
export async function mountWorkspaceTaskList(container, options = {}) {
  resetState();
  const owner = state;
  state.navId = getNavigationId();
  state.root = typeof container === 'string' ? document.querySelector(container) : container;
  if (!state.root) return () => {};

  await loadWorkspaceTaskData(options);
  if (!isLive(owner)) return undefined;
  state.root.innerHTML = renderWorkspaceTaskList(options);
  refreshFilterBar();
  bindListEvents();

  await fetchTasks();

  // Called by the All Tasks page's teardown and by the dashboard's. Either can run late (router.js
  // runs a late teardown at once), when a newer mount of this list owns the module state.
  return () => {
    if (state === owner) cleanupWorkspaceTaskList();
  };
}

// ── Page entry ──

export async function workspaceTasksPage() {
  const navId = getNavigationId();
  if (!(await checkAuth()) || getNavigationId() !== navId) return;
  initLayout(getUser());
  updateLayoutContext({ projectId: null, projectName: null, activeView: 'tasks', viewLabel: 'All Tasks' });

  return mountWorkspaceTaskList(getContentEl());
}
